/* Chart token bridge — keeps hand-rolled SVG in lockstep with theme.
   Always consume colours via these constants (or fill-* Tailwind
   classes), never as inline hex literals. */

import type { ClassRoom, District, School, Student, TermId, YearLevel } from "@/types";
import { getStudentAssessment } from "./report-data";
import type { RiskBand } from "./copy";

export const RISK_FILL: Record<RiskBand, string> = {
  low:       "var(--chart-risk-low)",
  moderate:  "var(--chart-risk-moderate)",
  high:      "var(--chart-risk-high)",
  more_info: "var(--chart-risk-insufficient)",
};

export const BRAND_CATEGORICAL = [
  "var(--chart-1)", // aubergine
  "var(--chart-2)", // purple
  "var(--chart-3)", // teal
  "var(--chart-4)", // gold
  "var(--chart-5)", // salmon
] as const;

export const DISTRIBUTION_FILL = "var(--chart-distribution)"; // mauve

export const CHART_AXIS = {
  line: "var(--ink-300)",
  tick: "var(--ink-500)",
  grid: "var(--ink-150)",
  label: "var(--ink-700)",
} as const;

export type PositionScope = "class" | "school" | "population";

function isFiniteCompositeScore(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function yearLabel(year: YearLevel): string {
  return year === "F" ? "Foundation" : "Year 1";
}

export function comparisonStudentsForScope(
  student: Student,
  klass: ClassRoom,
  school: School,
  districts: District[],
  compareScope: PositionScope,
  term: TermId | string,
): Student[] {
  const sameYear = (candidate: Student) => candidate.year === student.year;
  const hasAvailableCompositeScore = (candidate: Student) =>
    isFiniteCompositeScore(getStudentAssessment(candidate, term).compositeScore);

  const candidates =
    compareScope === "class"
      ? klass.students
      : compareScope === "school"
        ? school.classes.flatMap((candidateClass) => candidateClass.students)
        : districts.flatMap((district) =>
            district.schools.flatMap((candidateSchool) =>
              candidateSchool.classes.flatMap((candidateClass) => candidateClass.students),
            ),
          );

  return candidates.filter((candidate) => sameYear(candidate) && hasAvailableCompositeScore(candidate));
}

export function compositeScoreDistributionValues(
  students: Student[],
  term: TermId | string,
): number[] {
  return students
    .map((student) => getStudentAssessment(student, term).compositeScore)
    .filter(isFiniteCompositeScore);
}

function gaussianKernel(distance: number, bandwidth: number): number {
  const scaled = distance / bandwidth;
  return Math.exp(-0.5 * scaled * scaled);
}

export function buildDensityPath(
  values: number[],
  axisMin: number,
  axisMax: number,
  width: number,
  height: number,
  steps = 72,
  bandwidth = 6,
): string | null {
  if (values.length < 2 || steps < 2 || width <= 0 || height <= 0) return null;

  const clamped = values.map((value) => Math.max(axisMin, Math.min(axisMax, value)));
  const uniqueCount = new Set(clamped.map((value) => value.toFixed(3))).size;
  const effectiveBandwidth = uniqueCount <= 1 ? Math.max(2, bandwidth / 2) : bandwidth;
  const axisRange = axisMax - axisMin;
  const xToPx = (value: number) => ((value - axisMin) / axisRange) * width;

  const samples: Array<{ px: number; density: number }> = [];
  let maxDensity = 0;
  for (let step = 0; step <= steps; step += 1) {
    const x = axisMin + (step / steps) * axisRange;
    const density = clamped.reduce(
      (sum, value) => sum + gaussianKernel(x - value, effectiveBandwidth),
      0,
    );
    samples.push({ px: xToPx(x), density });
    maxDensity = Math.max(maxDensity, density);
  }

  if (maxDensity <= 0) return null;

  let path = "";
  for (let index = 0; index < samples.length; index += 1) {
    const sample = samples[index];
    if (!sample) continue;
    const py = height - (sample.density / maxDensity) * height;
    path += `${index === 0 ? "M" : "L"} ${sample.px.toFixed(1)} ${py.toFixed(1)} `;
  }
  return path.trim();
}

export function scopeLabel(
  scopeKind: PositionScope,
  student: Student,
  klass: ClassRoom,
  school: School,
): string {
  const cohortLabel = yearLabel(student.year);
  if (scopeKind === "class") return `${cohortLabel} students in class ${klass.name}`;
  if (scopeKind === "school") return `${cohortLabel} students in ${school.name}`;
  return `all ENSSA ${cohortLabel} students`;
}
